import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createWSClient, TRPCClientError, wsLink } from '@trpc/client';
import { ReactNode, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { ENV } from '../config/env';
import { supabase } from '../config/supabase';
import { trpc } from '../config/trpc';
import { useConnection } from '../context/ConnectionProvider';

type Props = {
  children: ReactNode;
};

export const TRPC = ({ children }: Props) => {
  const { onConnected, onDisconnected } = useConnection();
  const toast = useToast();
  const [token] = useLocalStorage('accessToken', '');

  const [trpcClient, setTrpcClient] = useState<any>(null);
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: async (error) => {
        Sentry.captureException(error);
        if (!(error instanceof TRPCClientError)) return console.log(error);

        const { code } = error?.data || {};
        if (code === 'UNAUTHORIZED') {
          if (error.message === 'Permission denied') {
            toast({
              title: 'Unauthorized',
              description: `You do not have permission to view this page.`,
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
          } else {
            return supabase.auth.refreshSession();
          }
        }
        if (code === 'FORBIDDEN') {
          toast({
            title: 'Unauthorized',
            description: `You do not have permission to view this page.`,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  useEffect(() => {
    const wsClient = createWSClient({
      url: ENV.WS_API_URL + `?jwt=${token}`,
      onClose: onDisconnected,
      onOpen: onConnected,
    });

    const nextTrpcClient = trpc.createClient({
      links: [wsLink({ client: wsClient })],
      transformer: undefined,
    });

    setTrpcClient(nextTrpcClient);

    return () => wsClient.close();
  }, [onConnected, onDisconnected, token]);

  if (!trpcClient) return null;

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
};
